import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import useCloudSourceForm from '../CloudSourceHooks/useCloudSourceForm.hooks';

/**
 * useRenderersState provides state and configuration for specialized components and dynamic fields.
 *
 * Responsibilities:
 * - Managing ingest tab selection and error state flags
 * - Providing configuration for components to use
 *
 * This hook returns only state, handlers, and flags - no JSX.
 * Components consume this hook's data directly.
 */
export const useRenderersState = () => {
  const { formState } = useFormContext();
  const { errors } = formState;

  const {
    connectionInputSchema,
    selectedIngestConfigTab,
    setSelectedIngestConfigTab,
    isResettingSourceType,
    mode,
    selectedSourceTypeId
  } = useCloudSourceForm();

  const handleIngestConfigDataChange = (newValue: string) => {
    setSelectedIngestConfigTab(newValue);
  };

  // Check for errors in fields other than data_provider (Ingest Method Config tab)
  const hasIngestMethodErrors = useMemo(() => {
    if (!errors || Object.keys(errors).length === 0) return false;

    // Check if there are any errors excluding data_provider and data_provider_selection
    const hasErrors = Object.keys(errors).some(
      (key) =>
        key !== 'data_provider' &&
        key !== 'data_provider_selection' &&
        errors[key]
    );

    return hasErrors;
  }, [errors, formState]);

  // Check for data provider specific errors (Data Provider tab)
  const hasDataProviderError = useMemo(() => {
    if (!connectionInputSchema?.is_container_ingest_enabled) return false;
    if (!errors || Object.keys(errors).length === 0) return false;

    // Check if there are errors in data_provider or data_provider_selection fields
    const hasErrors = Boolean(
      errors?.data_provider || errors?.data_provider_selection
    );

    return hasErrors;
  }, [errors, connectionInputSchema?.is_container_ingest_enabled, formState]);

  return {
    // Configuration
    connectionInputSchema,
    selectedSourceTypeId,
    selectedIngestConfigTab,
    isResettingSourceType,
    mode,

    // Handlers
    handleIngestConfigDataChange,

    // Error flags
    hasIngestMethodErrors,
    hasDataProviderError
  };
};
